export const palette = {
  // Fluent 2 Brand (matched to mobile)
  accent: '#0F6CBD',
  accentDeep: '#115EA3',
  accentWash: '#EBF3FC',
  accentSoft: '#D4E8FB',
  white: '#FFFFFF',
  bg: '#F7F8FA',
  g50: '#FAFBFC',
  g100: '#F0F0F0',
  g150: '#E9ECEF',
  g200: '#E5E5EA',
  g300: '#D1D1D6',
  g400: '#AEAEB2',
  g500: '#8E8E93',
  g600: '#636366',
  g700: '#48484A',
  g800: '#1A1A1A',
  g900: '#141414',
  green: '#107C10',
  greenWash: '#DFF6DD',
  amber: '#CA5010',
  amberWash: '#FFF1DB',
  red: '#D13438',
  redWash: '#FDE7E9',
  blue: '#3B82F6',
  blueWash: '#EFF6FF',
  purple: '#5B5FC7',
  purpleWash: '#E8E8FC',
  indigo: '#818CF8',
  indigoWash: '#EEF2FF',
} as const;

export const roleColor = {
  owner: palette.accent,
  manager: palette.accentDeep,
  employee: palette.green,
  contractor: palette.amber,
  operator: palette.blue,
} as const;

// Plus Jakarta Sans loaded via Google Fonts in index.html.
// System fonts kept as fallback for instant render before Plus Jakarta Sans loads.
export const fontFamily = '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif';

// Shadows (layered for depth — card/cardHover matched to mobile Airbnb-glow)
export const shadow = {
  none: 'none',
  xs: '0 1px 2px rgba(0, 0, 0, 0.04)',
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0 2px 8px rgba(0, 0, 0, 0.08)',
  lg: '0 4px 16px rgba(0, 0, 0, 0.12)',
  card: '0 2px 16px rgba(0, 0, 0, 0.08)',
  cardHover: '0 4px 12px rgba(0, 0, 0, 0.08)',
  overlay: '0 -4px 20px rgba(0, 0, 0, 0.15)',
} as const;

// Transitions
export const transition = {
  fast: '150ms ease',
  normal: '200ms ease',
  slow: '300ms ease',
} as const;

// Admin-specific colours
export const admin = {
  bg: '#F7F8FA',
  surface: '#FFFFFF',
  border: '#E5E7EB',
  borderHover: '#C8D5E3',
  muted: '#9CA3AF',
  header: '#1A1A1A',
  body: '#374151',
  caption: '#8E8E93',
  cardRadius: 16,
} as const;

// ── Premium tokens (Session 12) ──

export const sidebar = {
  bg: '#0C1929',
  bgHover: '#162741',
  bgActive: '#1A3355',
  text: 'rgba(255,255,255,0.55)',
  textHover: 'rgba(255,255,255,0.8)',
  textActive: '#FFFFFF',
  border: 'rgba(255,255,255,0.06)',
  borderSubtle: 'rgba(255,255,255,0.08)',
  surfaceSubtle: 'rgba(255,255,255,0.04)',
  width: 240,
  widthCollapsed: 64,
  accentBar: '#0F6CBD',
} as const;

export const topbar = {
  height: 56,
  bg: '#FFFFFF',
  searchBg: '#F8F9FB',
  searchBorder: '#E5E7EB',
} as const;

export const metricCard = {
  bg: '#FFFFFF',
  border: '#E5E7EB',
  borderHover: '#C8D5E3',
  labelSize: 12,
  valueSize: 26,
  valueFontWeight: 600,
  valueLetterSpacing: '-0.03em',
  trendUpColor: '#059669',
  trendDownColor: '#DC2626',
} as const;

export const statusDot = {
  active: '#059669',
  idle: '#D97706',
  off: '#D1D5DB',
  size: 10,
  borderWidth: 2,
} as const;

export const urgency = {
  critical: '#DC2626',
  warning: '#D97706',
  info: '#0F6CBD',
  dotSize: 8,
} as const;

export const activity = {
  timeColor: '#8E8E93',
  timeWidth: 48,
  textColor: '#636366',
  nameColor: '#1A1A1A',
  nameFontWeight: 550,
} as const;

export const scheduleColors = {
  job: '#059669',
  booking: '#D97706',
  shift: '#0F6CBD',
  event: '#8E8E93',
  personal: '#D1D5DB',
  barWidth: 3,
  barRadius: 2,
} as const;

// ── Settings Hub tokens (Session 21) ──

export const settingsHub = {
  // Card icon backgrounds
  iconBlueBg: '#EBF3FC',
  iconBlueFg: '#0F6CBD',
  iconGreenBg: '#E1F5EE',
  iconGreenFg: '#0F6E56',
  iconPurpleBg: '#EEEDFE',
  iconPurpleFg: '#534AB7',
  iconOrangeBg: '#FAECE7',
  iconOrangeFg: '#993C1D',
  iconAmberBg: '#FAEEDA',
  iconAmberFg: '#854F0B',
  iconLimeBg: '#EAF3DE',
  iconLimeFg: '#3B6D11',
  // Completion badges
  badgeDoneBg: '#EAF3DE',
  badgeDoneFg: '#3B6D11',
  badgeSetupBg: '#FAEEDA',
  badgeSetupFg: '#854F0B',
  // Progress bar urgency
  urgencyCalm: '#EBF3FC',
  urgencyNudge: '#EBF3FC',
  urgencyUrgent: '#FAEEDA',
  urgencyExpired: '#FEF2F2',
} as const;

export const sparkline = {
  barColor: '#0F6CBD',
  barWidth: 6,
  barGap: 2,
  barRadius: 2,
  height: 28,
  opacityBase: 0.2,
  opacityRecent: 0.55,
  opacityCurrent: 0.85,
} as const;

// ── Design scales (matched to mobile — Phase 2) ──

export const typography = {
  fontFamily,

  size: {
    micro: 11,
    caption: 13,
    body: 15,
    subheading: 15,
    heading: 17,
    title: 22,
    hero: 34,
  },

  weight: {
    regular: '400' as const,
    medium: '500' as const,
    semibold: '600' as const,
    bold: '700' as const,
  },

  lineHeight: {
    tight: 1.2,
    normal: 1.4,
    relaxed: 1.6,
  },
} as const;

export const spacing = {
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 40,
  xxxl: 48,
} as const;

export const radii = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  full: 9999,
} as const;
